import { ChatDocument, ChatDocumentSchema } from '@util/types/firestore/chat';
import { onSnapshot } from 'firebase/firestore';
import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { getChatsByUidQuery } from '@util/firestore/messages/';
import { reportDashboardError } from '@util/reportDashboardError';

const useRealtimeChats = (userid?: string) => {
  const [realtimeChats, setChats] = useState<ChatDocument[] | null>(null);
  const [chatsError, setChatsError] = useState<Error | null>(null);
  // Bumping this re-runs the effect, which tears down the failed listener and
  // opens a fresh one. It is what the retry button on the messages page does.
  const [attempt, setAttempt] = useState(0);
  const chatsQuery = useMemo(
    () => (userid ? getChatsByUidQuery(userid) : null),
    [userid]
  );

  const subscribedQueryRef = useRef<unknown>(null);

  useEffect(() => {
    // Only a NEW subscription target invalidates what we hold: the previous
    // snapshot no longer describes this user, so the answer goes back to
    // unknown rather than staying at the last user's result.
    //
    // A retry for the SAME user must not clear it. Reconnecting after a
    // failure would otherwise throw away conversations the user can still
    // read, and if the retry failed too they would drop from the stale-inbox
    // banner to a full-page error — worse off for having pressed the button.
    if (subscribedQueryRef.current !== chatsQuery) {
      subscribedQueryRef.current = chatsQuery;
      setChats(null);
      setChatsError(null);
    }

    // No uid, no subscription. ChatProvider wraps every page — including for
    // signed-out visitors — and `allow list: if isSignedIn()` on the messages
    // collection denies them, so subscribing anyway meant a guaranteed
    // permission error on every anonymous pageview.
    if (!chatsQuery) return;

    // unsubscribe() stops future deliveries but does not cancel a callback
    // that is already queued, so without this guard a snapshot for the
    // previous user could paint under the next one. Same guard AuthContext
    // uses for its user-document listener.
    let isCurrentListener = true;

    const unsubscribe = onSnapshot(
      chatsQuery,
      (snapshot) => {
        if (!isCurrentListener) return;
        setChatsError(null);
        if (snapshot.empty) {
          setChats([]);
          return;
        }
        const realTimeData = snapshot.docs
          .map((doc) => {
            const data = doc.data();
            try {
              return ChatDocumentSchema.parse(data);
            } catch (e) {
              console.error('Invalid chat document:', e);
              return null;
            }
          })
          .filter((chat): chat is ChatDocument => chat !== null);
        setChats(realTimeData);
      },
      (err) => {
        if (!isCurrentListener) return;
        console.warn('[useRealtimeChats] snapshot error:', err);
        // Previously swallowed: a permission or connectivity failure here
        // leaves the inbox with no data and produced no Sentry event, so a
        // broken messages page was indistinguishable from an empty one.
        reportDashboardError('useRealtimeChats: chats snapshot failed', err);
        setChatsError(err);
      }
    );
    return () => {
      isCurrentListener = false;
      unsubscribe();
    };
  }, [chatsQuery, attempt]);

  const retryChats = useCallback(() => setAttempt((a) => a + 1), []);

  return {
    realtimeChats,
    chatsError,
    retryChats,
  };
};

export default useRealtimeChats;
